import React, { useEffect, useState } from 'react';

import { Button, Group, Modal, Radio, Stack, Text, Title } from '@mantine/core';

import {
  ApplyScopeField,
  applyScopePackageOptionDescription,
  applyScopePromptSubject,
} from './applyScopeMessages';

export type ApplyScope = 'invoice_only' | 'package';

interface IApplyScopeModalProps {
  opened: boolean;
  changedFields: ApplyScopeField[];
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (scope: ApplyScope) => void;
}

/**
 * Asks whether an invoice edit applies to that invoice or to the rest of the package.
 *
 * Named for the scope choice rather than for auto-pay, because Product widened the prompt to
 * cover the payment method too. There is one prompt rather than one per field, since the staff
 * member is expressing a single intent about a single membership, so the copy names whichever
 * fields they changed.
 */
const ApplyScopeModal = ({
  opened,
  changedFields,
  isSaving,
  onCancel,
  onConfirm,
}: IApplyScopeModalProps): JSX.Element => {
  // Nothing preselected, so Save stays disabled until the staff member says which they meant.
  // The whole point of the prompt is that we do not know, and a default would guess for them.
  const [scope, setScope] = useState<ApplyScope | null>(null);

  // The component stays mounted between edits, so without this the next prompt would open on the
  // last answer with Save already enabled.
  useEffect(() => {
    if (!opened) {
      setScope(null);
    }
  }, [opened]);

  const handleCancel = (): void => {
    setScope(null);
    onCancel();
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCancel}
      title={<Title order={4}>Apply Change</Title>}
      size={460}
    >
      <Stack p='md' spacing='lg'>
        <Text size='sm'>Where would you like to apply {applyScopePromptSubject(changedFields)}?</Text>
        <Radio.Group value={scope ?? ''} onChange={value => setScope(value as ApplyScope)}>
          <Stack spacing='md'>
            <Stack spacing={4}>
              <Radio value='invoice_only' label='This invoice only' color='kiloBlue' size='md' />
              <Text size='xs' color='dimmed' pl={34}>
                Only the invoice you are editing is affected.
              </Text>
            </Stack>
            <Stack spacing={4}>
              <Radio value='package' label='For the rest of this package' color='kiloBlue' size='md' />
              <Text size='xs' color='dimmed' pl={34}>
                {applyScopePackageOptionDescription(changedFields)}
              </Text>
            </Stack>
          </Stack>
        </Radio.Group>
        <Group position='right'>
          <Button variant='subtle' onClick={handleCancel}>
            Cancel
          </Button>
          <Button disabled={scope === null} loading={isSaving} onClick={() => scope && onConfirm(scope)}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ApplyScopeModal;
